import { useState } from "react";
import { lettersApi, ApiError } from "@/api";
import type { LetterListRow, LetterSource } from "@/types";
import { Spinner, Pill } from "@/components/ui";
import { humanise, letterConfidenceTone, letterSourceLabel, letterSourceTone } from "@/lib/jobs";
import { formatDateShort } from "@/lib/format";

/** Expandable per-application letter list (Brief 06 §3.1). Fetches the
 *  application's letters on first expand (read-only; no receipts). Rows open the
 *  full detail via onOpen(id, source). */
export function AppLetters({
  applicationId,
  onOpen,
}: {
  applicationId: number;
  onOpen: (id: number, source: LetterSource) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [letters, setLetters] = useState<LetterListRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  function toggle() {
    const next = !expanded;
    setExpanded(next);
    if (next && letters === null && !loading) {
      setLoading(true);
      setError(undefined);
      lettersApi
        .listLetters({ application_id: applicationId, sort_field: "letter_date", sort_direction: "desc", per_page: 100 })
        .then((res) => setLetters(res.items ?? []))
        .catch((err) => setError(err instanceof ApiError ? err.message : "Could not load letters."))
        .finally(() => setLoading(false));
    }
  }

  return (
    <div className="app-letters">
      <button type="button" className="app-letters__toggle" onClick={toggle} aria-expanded={expanded}>
        {expanded ? "Hide letters" : "View letters"}
      </button>

      {expanded ? (
        loading ? (
          <div className="app-letters__loading"><Spinner label="Loading letters…" /></div>
        ) : error ? (
          <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p>
        ) : letters && letters.length > 0 ? (
          <ul className="app-letter-list">
            {letters.map((l) => (
              <li key={`${l.source}-${l.id}`}>
                <button type="button" className="app-letter" onClick={() => onOpen(l.id, l.source)}>
                  <span className="app-letter__line">
                    <Pill tone={letterSourceTone(l.source)}>{letterSourceLabel(l.source)}</Pill>
                    <span className="app-letter__type">
                      {humanise(l.letter_type)}{l.letter_subtype ? ` · ${humanise(l.letter_subtype)}` : ""}
                    </span>
                  </span>
                  <span className="app-letter__meta">
                    <span className="job-drawer__muted">
                      {formatDateShort(l.letter_date)}{l.addressed_to ? ` · ${l.addressed_to}` : ""}
                    </span>
                    {l.confidence ? <Pill tone={letterConfidenceTone(l.confidence)}>{humanise(l.confidence)}</Pill> : null}
                    {l.has_linked_task ? <span className="app-letter__task">↪ task</span> : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="job-drawer__muted">No letters yet.</p>
        )
      ) : null}
    </div>
  );
}
